import { GoogleAnalytics4 } from "@utils/google-analytics-4";
import { LocalStorageWithExpire } from "@utils/local-storage-with-expire";
import { ResponsiveHelper } from "@utils/responsive-helper";
import autobind from "autobind-decorator";
import { PromisePool, getLocale } from "bm-ui-kit";
import React from "react";
import type { ReactNode } from "react";
import { Ad } from "../Ad/Ad";
import type { Variant } from "../Menu/type";
import { ArticleFooterNewsletterSubscribe } from "./ArticleFooterNewsletterSubscribe";
import { InterstitialBlock } from "./InterstitialBlock";
import { ScrollButton } from "./ScrollButton";

export const FOOTER_SUBSCRIBE_LOCAL_STORAGE = "bm-footer-subscribe";
const INTERSTITIAL_LOCAL_STORAGE = "bm-interstitial";

interface Props {
	variant: Variant;
}

interface State {
	showFooterSubscribe: boolean;
	showInterstitial: boolean;
	showScrollButton: boolean;
	isArticle: boolean;
	isHomePage: boolean;
}

export class FooterFeaturesHandler extends React.Component<Props, State> {
	private readonly promisePool = new PromisePool();
	private readonly ga4InstanceId = GoogleAnalytics4.generateId("id");
	public constructor(props: Props) {
		super(props);

		this.state = {
			showFooterSubscribe: false,
			showInterstitial: false,
			showScrollButton: false,
			isArticle: !!window.location.href.match(".+/mediatar/.*")?.length,
			isHomePage: window.location.pathname === "/",
		};
	}

	public componentDidMount(): void {
		const { isArticle } = this.state;

		if (isArticle && !ResponsiveHelper.mediaBreakPointDown("lg")) {
			const isInterstitialClosed = LocalStorageWithExpire.get(INTERSTITIAL_LOCAL_STORAGE) === "closed";
			const isFooterSubscribeClosed = LocalStorageWithExpire.get(FOOTER_SUBSCRIBE_LOCAL_STORAGE) === "closed";

			if (!isInterstitialClosed) {
				this.setState({
					showInterstitial: true,
				});
			} else if (!isFooterSubscribeClosed) {
				this.startFooterTimer();
			}
		}
	}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	public render(): ReactNode {
		const { variant } = this.props;
		const { showInterstitial, showFooterSubscribe, isArticle, isHomePage } = this.state;
		const isHun: boolean = getLocale() === "hu_HU";
		const isDataForm = window.location.pathname.includes("/adatbekero/");

		return (
			<>
				{showInterstitial && <InterstitialBlock onClosed={this.onInterstitialClosed} />}
				{(isHomePage || (isArticle && !showFooterSubscribe)) && (
					<div className="bg-light fixed-bottom w-100 d-flex justify-content-center">
						<Ad name="970x30" width="970" height="30" code="7394016" type="desktop" gapeless withoutLabel />
						<Ad
							name="360x50"
							width="360"
							height="50"
							code="7416031"
							type="non-desktop"
							gapeless
							withoutLabel
						/>
					</div>
				)}
				{showFooterSubscribe && (
					<ArticleFooterNewsletterSubscribe
						onClosed={this.onFooterSubscribeClose}
						onSubscribe={this.onSubscribe}
						subscriptionTypes={variant === "corporate" ? ["CORPORATE_NEWSLETTER"] : ["RETAIL_NEWSLETTER"]}
					/>
				)}
				{isHun && !showInterstitial && !showFooterSubscribe && !isDataForm && (
					<ScrollButton onShow={this.onScrollButtonShow} />
				)}
			</>
		);
	}

	@autobind
	private onFooterSubscribeClose(): void {
		LocalStorageWithExpire.set(FOOTER_SUBSCRIBE_LOCAL_STORAGE, "closed", 7 * 24 * 60); // 7 days
		this.setState({
			showFooterSubscribe: false,
		});
	}

	@autobind
	private onSubscribe(email: string): void {
		LocalStorageWithExpire.set(FOOTER_SUBSCRIBE_LOCAL_STORAGE, "closed", 30 * 24 * 60); // 30 days

		GoogleAnalytics4.sendEvent("BM_newsletterSubscription", {
			instanceId: this.ga4InstanceId,
			componentName: "FooterFeaturesHandler",
			newsletterType: "general",
			email,
			user_data: {
				email_address: email,
			},
		});
	}

	@autobind
	private onInterstitialClosed(hasBanner: boolean): void {
		const { isArticle } = this.state;
		const isFooterSubscribeClosed = LocalStorageWithExpire.get(FOOTER_SUBSCRIBE_LOCAL_STORAGE) === "closed";

		if (hasBanner) {
			LocalStorageWithExpire.set(INTERSTITIAL_LOCAL_STORAGE, "closed", 7 * 24 * 60); // 7 days
		}

		this.setState({
			showInterstitial: false,
		});

		if (isArticle && !isFooterSubscribeClosed) {
			this.startFooterTimer();
		}
	}

	private startFooterTimer(): void {
		this.promisePool.addTimeout(7_000).then(() => {
			const { showScrollButton } = this.state;

			if (!showScrollButton) {
				this.setState({
					showFooterSubscribe: true,
				});
			}
		});
	}

	@autobind
	private onScrollButtonShow(show: boolean): void {
		const { isArticle } = this.state;
		const isFooterSubscribeClosed = LocalStorageWithExpire.get(FOOTER_SUBSCRIBE_LOCAL_STORAGE) === "closed";

		this.setState({
			showScrollButton: show,
		});

		if (!show && isArticle && !isFooterSubscribeClosed) {
			this.startFooterTimer();
		}
	}
}
